import React, { useEffect, useState } from 'react';
import SITE from '../api';
import { useSite, IMG, GooglePlayBadge } from '../components/StoreLayout';
import useSEO from '../useSEO';

function ytEmbedUrl(url) {
  if (!url) return '';
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([a-zA-Z0-9_-]{6,})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : '';
}

export default function MobileApp() {
  const { content: c } = useSite();
  const [shots, setShots] = useState([]);
  const [active, setActive] = useState(0);

  useEffect(() => { SITE.get('/app-screenshots').then(r => setShots(r.data.screenshots || [])); }, []);

  // auto-advance the hero carousel every 3.5s
  useEffect(() => {
    if (shots.length < 2) return;
    const t = setInterval(() => setActive(i => (i + 1) % shots.length), 3500);
    return () => clearInterval(t);
  }, [shots.length]);

  const reviews = [1, 2, 3]
    .map(n => ({ text: c[`mobileapp.review_${n}_text`], author: c[`mobileapp.review_${n}_author`] }))
    .filter(r => r.text);

  const tags = (c['mobileapp.tags'] || '').split(',').map(t => t.trim()).filter(Boolean);

  const embedSrc = ytEmbedUrl(c['mobileapp.youtube_url']);

  useSEO({
    title: c['mobileapp.meta_title'] || c['mobileapp.heading'] || 'Mobile App',
    description: c['mobileapp.meta_description'] || c['mobileapp.subtext'],
    keywords: c['mobileapp.meta_keywords'],
    canonical: `${window.location.origin}/store/mobile-app`,
  });

  return (
    <div>
      <style>{`
        .sf-app-hero { display: grid; grid-template-columns: 340px 1fr; gap: 50px; align-items: center; }
        .sf-app-stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
        .sf-app-gallery { display: flex; gap: 16px; overflow-x: auto; padding-bottom: 10px; }
        .sf-app-reviews { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 18px; }
        @media (max-width: 760px) {
          .sf-app-hero { grid-template-columns: 1fr; }
          .sf-app-stats { grid-template-columns: 1fr 1fr 1fr; gap: 10px; }
        }
      `}</style>

      {/* hero */}
      <section style={{ maxWidth: 1100, margin: '0 auto', padding: '44px 24px 10px' }}>
        <div className="sf-app-hero">
          <div style={{ justifySelf: 'center', width: '100%', maxWidth: 300 }}>
            <div style={{ position: 'relative', borderRadius: 28, overflow: 'hidden', boxShadow: '0 20px 50px rgba(0,0,0,0.18)', border: '8px solid #191919', background: '#fafafa', aspectRatio: '9/16' }}>
              {shots.length > 0 ? (
                shots.map((s, i) => (
                  <img key={s.id} src={IMG(s.image)} alt={s.caption || 'App screenshot'} loading={i === 0 ? undefined : 'lazy'}
                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: i === active ? 1 : 0, transition: 'opacity .6s ease' }} />
                ))
              ) : (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bbb', fontSize: 13, textAlign: 'center', padding: 16 }}>
                  App screenshots coming soon
                </div>
              )}
            </div>
            {shots.length > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 14 }}>
                {shots.map((s, i) => (
                  <button key={s.id} onClick={() => setActive(i)} aria-label={`Show screenshot ${i + 1}`}
                    style={{ width: i === active ? 18 : 7, height: 7, borderRadius: 4, border: 'none', cursor: 'pointer', background: i === active ? 'var(--sf-accent)' : '#ddd', transition: 'width .2s ease' }} />
                ))}
              </div>
            )}
          </div>

          <div>
            <h1 style={{ fontSize: 30, fontWeight: 700, margin: '0 0 12px', color: '#191919' }}>
              {c['mobileapp.heading'] || 'Shop Faster with the Salvador Eyewear App'}
            </h1>
            {c['mobileapp.subtext'] && <p style={{ fontSize: 15.5, color: '#555', lineHeight: 1.6, margin: '0 0 22px' }}>{c['mobileapp.subtext']}</p>}

            <div className="sf-app-stats" style={{ marginBottom: 24 }}>
              {c['mobileapp.downloads_count'] && (
                <Stat icon="ti-download" value={c['mobileapp.downloads_count']} label="Downloads" />
              )}
              {c['mobileapp.rating_value'] && (
                <Stat icon="ti-star-filled" value={c['mobileapp.rating_value']} label="Rating" stars />
              )}
              {c['mobileapp.rating_count'] && (
                <Stat icon="ti-message-star" value={c['mobileapp.rating_count']} label="Reviews" />
              )}
            </div>

            <GooglePlayBadge c={c} height={54} />

            {c['mobileapp.description'] && (
              <p style={{ fontSize: 14, color: '#666', lineHeight: 1.8, marginTop: 24, whiteSpace: 'pre-line' }}>{c['mobileapp.description']}</p>
            )}
          </div>
        </div>
      </section>

      {/* video */}
      {embedSrc && (
        <section style={{ maxWidth: 720, margin: '40px auto', padding: '0 24px' }}>
          <div style={{ position: 'relative', paddingTop: '56.25%', borderRadius: 16, overflow: 'hidden', boxShadow: '0 10px 34px rgba(0,0,0,0.14)' }}>
            <iframe
              src={embedSrc}
              title="Salvador Eyewear App"
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </section>
      )}

      {/* screenshot gallery */}
      {shots.length > 0 && (
        <section style={{ maxWidth: 1100, margin: '20px auto 50px', padding: '0 24px' }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 18, textAlign: 'center' }}>App Screenshots</h2>
          <div className="sf-app-gallery">
            {shots.map(s => (
              <div key={s.id} style={{ flex: '0 0 auto', width: 170 }}>
                <div style={{ borderRadius: 16, overflow: 'hidden', border: '1px solid #eee', boxShadow: '0 4px 16px rgba(0,0,0,0.06)', aspectRatio: '9/16', background: '#fafafa' }}>
                  <img src={IMG(s.image)} alt={s.caption || 'App screenshot'} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                </div>
                {s.caption && <div style={{ fontSize: 12, color: '#777', textAlign: 'center', marginTop: 8 }}>{s.caption}</div>}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* reviews */}
      {reviews.length > 0 && (
        <section style={{ background: '#f7f8fa', padding: '46px 0' }}>
          <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 24px' }}>
            <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 22, textAlign: 'center' }}>What Our Customers Say</h2>
            <div className="sf-app-reviews">
              {reviews.map((r, i) => (
                <div key={i} style={{ background: '#fff', border: '1px solid #eee', borderRadius: 14, padding: 22 }}>
                  <div style={{ color: 'var(--sf-accent)', marginBottom: 10, fontSize: 15 }}>
                    {'★★★★★'}
                  </div>
                  <p style={{ fontSize: 14, color: '#444', lineHeight: 1.7, marginBottom: 14 }}>&ldquo;{r.text}&rdquo;</p>
                  {r.author && <div style={{ fontSize: 13, fontWeight: 700, color: '#191919' }}>{r.author}</div>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* closing promo banner — the whole image links to the Play Store */}
      {c['mobileapp.bottom_banner_image'] && (
        <section style={{ maxWidth: 1100, margin: '10px auto 0', padding: '0 24px' }}>
          <a href={c['mobileapp.google_play_url'] || '#'} target="_blank" rel="noopener noreferrer" style={{ display: 'block' }}>
            <img src={IMG(c['mobileapp.bottom_banner_image'])} alt="Get the Salvador Eyewear app on Google Play" loading="lazy"
              style={{ width: '100%', height: 'auto', display: 'block', borderRadius: 14 }} />
          </a>
        </section>
      )}

      {/* keyword tags — plain informational chips, admin-editable */}
      {tags.length > 0 && (
        <section style={{ maxWidth: 1100, margin: '0 auto', padding: '40px 24px 60px' }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 18 }}>{c['mobileapp.tags_heading'] || 'People Are Also Looking For'}</h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {tags.map((t, i) => (
              <span key={i} style={{ background: 'var(--sf-body-bg, #f7f8fa)', border: '1px solid var(--sf-accent)', color: 'var(--sf-primary, #191919)', fontSize: 13, padding: '9px 16px', borderRadius: 20 }}>{t}</span>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function Stat({ icon, value, label, stars }) {
  return (
    <div style={{ background: '#f7f8fa', borderRadius: 12, padding: '14px 10px', textAlign: 'center' }}>
      <i className={`ti ${icon}`} style={{ fontSize: 20, color: 'var(--sf-accent)' }} />
      <div style={{ fontSize: 17, fontWeight: 700, color: '#191919', marginTop: 4 }}>
        {value}{stars && <span style={{ color: 'var(--sf-accent)', marginLeft: 3 }}>★</span>}
      </div>
      <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{label}</div>
    </div>
  );
}
